import { useContext, useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { IOrderDeliveryTask } from '../../../interfaces/delivery-company/tasks';
import { API_ENDPOINT } from '../../../configurations/global.config';
import axios from 'axios';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { timestampedMessage } from '../../common/multi-package-scanner/multi-package-scanner';
import {
  DeliveryBatchState,
  OrderDeliveryTaskState,
  PaymentTaskState,
} from '../../../enums/tasks';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import SnackBar from '../../common/snack-bar/snack-bar';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CustomDialog from '../../common/custom-dialog/custom-dialog';
import { isMobile, isTablet } from 'react-device-detect';
import { ClearIcon } from '@mui/x-date-pickers';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import CancelIcon from '@mui/icons-material/Cancel';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { showDeliveryTaskState } from '../../common/styles/delivery-task.styles';
import AddNotesDialog from '../common/add-notes-dialog';
import MarkAsFailedDialog from '../common/mark-as-failed-dialog';
import AddressWithIcon from '../../common/styles/address-with-icon';
import { displayMoney } from '../../../utils/money';
import { get_cod } from '../../../utils/cod';
import PaidIcon from '@mui/icons-material/Paid';
import { get_notes } from '../../../utils/notes';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeliveryTaskPage from '../driver-delivery-tasks/delivery-task-page';
import Order from '../../delivery-company-components/order/order';
import { Workflow } from '../../../enums/orders';
import { MerchantWithIcon } from '../../common/styles/merchant-with-icon';
import {
  IconWithText,
  mobileDoubleItemsGrid,
} from '../../common/styles/components';
import { blue, grey, red } from '@mui/material/colors';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { chipOpacity, chipPadding } from '../../common/styles/common';
import StaticGoogleMap from '../../common/static-google-map/static-google-map';
import { extractMapsPlaceLink } from '../../../utils/locations';
import SendMessageDialog from '../common/send-message-dialog/send-message-dialog';
import { CustomerWithIcon } from '../../common/styles/customer-with-icon';
import { showWorkflow } from '../../common/styles/workflow-display';
import OrderNotes from '../../common/order-notes/order-notes';
import ErrorIcon from '@mui/icons-material/Error';
import { DriverType } from '../../../interfaces/delivery-company/drivers';
import { buildResolveTemplateArgsForDelivery } from '../../delivery-company-components/settings/notification-manager/utils';

interface DeliveryTaskQuickScanDialogProps {
  packageId: string;

  onClose: () => void;
}
function DeliveryTaskQuickScanDialog({
  packageId,
  onClose,
}: DeliveryTaskQuickScanDialogProps) {
  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);

  const [deliveryTask, setDeliveryTask] = useState<IOrderDeliveryTask>();
  const [snackBar, setSnackBar] = useState<timestampedMessage>();
  const [whatsAppDialogOpen, setWhatsAppDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
  const [markAsFailedDialogOpen, setMarkAsFailedDialogOpen] = useState(false);
  const { user, config } = useContext(AuthContext);

  const handleAddNoteMenuClick = () => {
    console.log(deliveryTask);
    setAddNoteDialogOpen(true);
  };
  const handleAddNote = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/delivery-task/${deliveryTask?.id}/`;
    const requestData = {
      notes: [template],
    };
    const response = await axios.patch(endpoint, requestData, config);

    if (response.status == 200) {
      setDeliveryTask((prev): IOrderDeliveryTask => {
        if (!prev) throw new Error('deliveryTask is undefined');

        return {
          ...prev,
          notes: [...(prev.notes ?? []), template],
        };
      });
    }
    return response;
  };

  const handleMarkAsFailedMenuClick = () => {
    setMarkAsFailedDialogOpen(true);
  };

  const handleMarkAsFailedTasks = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/delivery-task/${deliveryTask?.id}/`;
    const requestData = {
      status: OrderDeliveryTaskState.FAILED,
      failure_reason: template,
    };

    const response = await axios.patch(endpoint, requestData, config);
    setDeliveryTask({
      ...deliveryTask!,
      status: OrderDeliveryTaskState.AWAITING_RETURN,
      failure_reason: template,
    });
    return response;
  };

  const handleStartTask = async (id: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/delivery-task/${id}/`;
    if (!deliveryTask) return false;
    await new Promise((f) => setTimeout(f, 1000));
    if (deliveryTask.status != OrderDeliveryTaskState.OUT_FOR_DELIVERY)
      return true;
    const requestData = {
      status: OrderDeliveryTaskState.IN_PROGRESS,
    };
    try {
      await axios.patch(endpoint, requestData, config);
      if (deliveryTask) {
        setDeliveryTask({
          ...deliveryTask,
          status: OrderDeliveryTaskState.IN_PROGRESS,
          failure_reason: undefined,
        });
        return true;
      }
    } catch (error) {
      return false;
    }

    return true;
  };

  const handleStartOrResumeTaskMenuClick = async () => {
    const endpoint = `${API_ENDPOINT}/api/v1/delivery-task/`;
    const requestData = {
      status: OrderDeliveryTaskState.IN_PROGRESS,
    };
    try {
      await axios.patch(endpoint + deliveryTask?.id + '/', requestData, config);
      if (deliveryTask) {
        const newValue = {
          timestamp: Date.now(),
          content: 'Task Started Successfully',
          level: 'success',
        };
        setSnackBar(newValue as timestampedMessage);
        setDeliveryTask({
          ...deliveryTask,
          status: OrderDeliveryTaskState.IN_PROGRESS,
          failure_reason: undefined,
        });
      }
    } catch (error) {
      const newValue = {
        timestamp: Date.now(),
        content: 'Could not Start Task',
        level: 'error',
      };
      setSnackBar(newValue as timestampedMessage);
    }
  };

  const handleSendWhatAppMessage = async (template: string) => {
    const endpoint = `${API_ENDPOINT}/api/v1/delivery-tasks/notify/`;
    const requestData = {
      message: template,
      task_ids: [deliveryTask?.id],
    };

    return axios.post(endpoint, requestData, config);
  };

  useEffect(() => {
    const fetchTask = async () => {
      let order: OrderType;
      setIsLoading(true);
      const orderId = packageId.slice(0, -4);
      try {
        const response = await axios.get<OrderType>(
          `${API_ENDPOINT}/api/v1/order/${orderId}/`,
          config
        );
        console.log(response.data);
        if (!response.data) {
          throw new Error('No order found');
        }
        order = response.data;
      } catch (e) {
        setSnackBar({
          timestamp: Date.now(),
          level: 'error',
          content: 'No order found',
        });
        setIsLoading(false);
        return;
      }

      if (!order.delivery_tasks) {
        setSnackBar({
          timestamp: Date.now(),
          level: 'error',
          content: 'Order not assigned for delivery',
        });
        setIsLoading(false);
        return;
      }

      if (order.delivery_tasks[0].status == OrderDeliveryTaskState.COMPLETED) {
        setSnackBar({
          timestamp: Date.now(),
          level: 'error',
          content: 'Order already delivered',
        });
        setIsLoading(false);
        return;
      }
      const last_delivery_task = order.delivery_tasks[0];

      last_delivery_task.order = order;

      setDeliveryTask(last_delivery_task);

      setIsLoading(false);
    };
    fetchTask();
  }, []);

  const shipmentNodes: JSX.Element[] = deliveryTask
    ? [
        <>
          <MerchantWithIcon
            key="merchant"
            merchant={deliveryTask.order.merchant}
            phone_number={deliveryTask.order.merchant_location?.contact_phone}
          />
          <StaticGoogleMap
            mapPath={extractMapsPlaceLink(deliveryTask.order.merchant_location)}
            url={deliveryTask?.order.merchant_location?.url}
            location={deliveryTask?.order.merchant_location}
            noMap={deliveryTask.order.workflow === Workflow.STANDARD}
          />
        </>,
        <>
          <CustomerWithIcon customer={deliveryTask.order.customer} showPhone />
          <StaticGoogleMap
            mapPath={extractMapsPlaceLink(deliveryTask.order.customer_location)}
            url={deliveryTask?.order.customer_location?.url}
            location={deliveryTask?.order.customer_location}
            noMap={deliveryTask.order.workflow === Workflow.RETURN}
          />
        </>,
      ]
    : [];

  return (
    <div>
      {isLoading ? (
        <div style={{ textAlign: 'center', width: '100%' }}>
          <CircularProgress />{' '}
        </div>
      ) : deliveryTask != undefined ? (
        <CustomDialog open={true} onClose={onClose} fullScreen={(isMobile && !isTablet)}>
          <DialogContent ref={containerRef}>
            <Typography variant="h3" textAlign="center">
              Order #{deliveryTask.order.order_id}
            </Typography>
            {/* <QRCodeBox value={ToQRcodeID({ id: packageId } as IMerchantOrdersInvoice)} showValue centered size={100} /> */}

            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
              }}
            >
              <IconButton onClick={onClose}>
                <ClearIcon color="primary" fontSize="large" />
              </IconButton>
            </div>

            <div style={{ textAlign: 'center', width: '100%' }}>
              {showDeliveryTaskState(deliveryTask.status)}
            </div>

            {deliveryTask.status == OrderDeliveryTaskState.IN_PROGRESS && (
              <div
                style={{ display: 'flex', justifyContent: 'center' }}
                onClick={scrollToBottom}
              >
                <Chip
                  icon={<QrCodeScannerIcon />}
                  label={<b>Complete Delivery</b>}
                  variant="filled"
                  style={{
                    textAlign: 'center',
                    margin: 4,
                    padding: chipPadding,
                    backgroundColor: alpha(grey[500], chipOpacity),
                  }}
                />
              </div>
            )}

            <>
              <hr />
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  mt: 0,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {deliveryTask.status ==
                  OrderDeliveryTaskState.OUT_FOR_DELIVERY && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleStartOrResumeTaskMenuClick()}
                  >
                    Start
                  </Button>
                )}
                {deliveryTask.status ==
                  OrderDeliveryTaskState.AWAITING_RETURN && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleStartOrResumeTaskMenuClick()}
                  >
                    Resume
                  </Button>
                )}

                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setWhatsAppDialogOpen(true)}
                >
                  Send
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleAddNoteMenuClick()}
                >
                  Add Note
                </Button>
                {deliveryTask.status != OrderDeliveryTaskState.COMPLETED &&
                  deliveryTask.status !=
                    OrderDeliveryTaskState.AWAITING_RETURN && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleMarkAsFailedMenuClick()}
                    >
                      Fail
                    </Button>
                  )}
              </Box>

              <hr />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {mobileDoubleItemsGrid([
                  <Typography key="first" variant="body1">
                    <strong>Delivery Task #</strong>
                    {deliveryTask.id}
                  </Typography>,
                  <Typography
                    key="second"
                    variant="body2"
                    color="text.secondary"
                  >
                    Created On:{' '}
                    {format(
                      new Date(deliveryTask.creation_date ?? ''),
                      'dd-MM-yyyy'
                    )}
                  </Typography>,
                ])}

                <div style={{ textAlign: 'center', width: '100%' }}>
                  {showWorkflow(
                    deliveryTask.order.workflow,
                    deliveryTask.order.is_exchange,
                    deliveryTask.order.exchange_for,
                    deliveryTask.order.return_reason
                  )}
                </div>
                {deliveryTask.order.workflow !== Workflow.RETURN && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      justifyContent: 'center',
                    }}
                  >
                    <PaidIcon color="primary" fontSize="small" />
                    <Typography variant="body1">
                      <strong>COD:</strong>{' '}
                      {displayMoney(get_cod(deliveryTask.order))}
                    </Typography>
                  </Box>
                )}

                <br />

                {deliveryTask.failure_reason && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      backgroundColor: 'error',
                      textAlign: 'center',
                      p: 1,
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <ErrorIcon color="error" fontSize="small" />
                    <Typography variant="body2" color="error.main">
                      {deliveryTask.failure_reason}
                    </Typography>
                  </Box>
                )}

                <br />
                <div style={{ width: '100%', textAlign: 'center' }}>
                  {deliveryTask.order.workflow == Workflow.STANDARD ? (
                    <>
                      <div>
                        <b>Ship To</b>
                        {shipmentNodes[1]}
                      </div>

                      <br />

                      <div>
                        <b>Ship From</b>
                        {shipmentNodes[0]}
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <b>Ship To</b>
                        {shipmentNodes[0]}
                      </div>

                      <br />

                      <div>
                        <b>Ship From</b>
                        {shipmentNodes[1]}
                      </div>
                    </>
                  )}
                </div>

                <br />
                <OrderNotes
                  order={deliveryTask.order}
                  active_task_notes={deliveryTask.notes}
                  showTitle
                />
              </Box>

              {deliveryTask.status == OrderDeliveryTaskState.IN_PROGRESS && (
                <DeliveryTaskPage
                  showActions={false}
                  taskId={deliveryTask.id}
                  onTaskScan={() =>
                    setDeliveryTask({
                      ...deliveryTask,
                      status: OrderDeliveryTaskState.COMPLETED,
                    })
                  }
                  reloadTasks={() => {
                    return;
                  }}
                />
              )}
            </>

            {whatsAppDialogOpen && (
              <SendMessageDialog
                setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
                onSubmit={handleSendWhatAppMessage}
                onClose={() => setWhatsAppDialogOpen(false)}
                stage={'Delivery'}
                startTask={handleStartTask}
                locallyResolvedTargets={[
                  {
                    id: deliveryTask.id,
                    label: `Order #${deliveryTask.order.order_id}`,
                    phone_number:
                      deliveryTask.order.workflow === Workflow.STANDARD
                        ? deliveryTask.order.customer?.phone_number ?? ''
                        : deliveryTask.order.merchant_location?.contact_phone ??
                          '',
                    resolveTemplateArgs: buildResolveTemplateArgsForDelivery(
                      deliveryTask.order,
                      user?.name ?? '',
                      user?.organisation?.org_id ?? 'ERROR'
                    ),
                    disabled: deliveryTask.order.workflow == Workflow.STANDARD && deliveryTask.order.customer?.notifications_disabled,
                    alreadyStarted:
                      deliveryTask.status !=
                      OrderDeliveryTaskState.OUT_FOR_DELIVERY,
                  },
                ]}
                openInWhatsapp={true}
                onSuccess={() => {
                  if (
                    deliveryTask.status ===
                    OrderDeliveryTaskState.OUT_FOR_DELIVERY
                  )
                    setDeliveryTask({
                      ...deliveryTask,
                      status: OrderDeliveryTaskState.IN_PROGRESS,
                    });
                }}
              />
            )}

            {addNoteDialogOpen && (
              <AddNotesDialog
                onSuccess={(r) => console.log()}
                setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
                onSubmit={handleAddNote}
                onClose={() => setAddNoteDialogOpen(false)}
              />
            )}
            {markAsFailedDialogOpen && (
              <MarkAsFailedDialog
                failure_reasons={[
                  'Shop closed',
                  'Wrong amount',
                  'Wrong invoice',
                  'Person absent',
                  'Invoice lost',
                ]}
                onSuccess={(r) =>
                  setDeliveryTask({
                    ...deliveryTask,
                    failure_reason: r,
                    status: OrderDeliveryTaskState.AWAITING_RETURN,
                  })
                }
                setSnackBarResult={(t?: timestampedMessage) => setSnackBar(t)}
                onSubmit={handleMarkAsFailedTasks}
                onClose={() => setMarkAsFailedDialogOpen(false)}
              />
            )}
          </DialogContent>
        </CustomDialog>
      ) : (
        <Alert severity="error">Delivery task not found.</Alert>
      )}
      {snackBar && snackBar.content && (
        <SnackBar
          message={snackBar.content}
          level={snackBar.level}
          key={snackBar.timestamp}
        />
      )}
    </div>
  );
}

export default DeliveryTaskQuickScanDialog;
